/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import {
    CacheType,
    createObservableDataAction,
    IAction,
    IActionContext,
    IActionInput,
    IAny,
    ICreateActionContext,
    IGeneric
} from '@msdyn365-commerce/core';
import { SearchOption } from '../common/search-option';

/**
 * Gets the search options.
 * @returns - List of search options.
 */
const getSearchOptions = () => async (ctx: IActionContext): Promise<SearchOption[]> => {
    return Promise.resolve([
        SearchOption.OrderNumber,
        SearchOption.ConfirmationId,
        SearchOption.ChannelName,
        SearchOption.CustomerRequisition
    ]);
};

/**
 * Action input.
 */
export class GetSearchOptionsInput implements IActionInput {
    public getCacheKey = () => 'OrderHistoryWithSearchAndFiltersSearchOptions';

    public getCacheObjectType = () => 'OrderHistoryWithSearchAndFiltersSearchOptions';

    public dataCacheType = (): CacheType => 'instance';
}

/**
 * Creates the input required to make the retail api call.
 * @param inputData - Action Data Input.
 * @returns - Input object.
 */
const createGetSearchOptionsInput = (inputData: ICreateActionContext<IGeneric<IAny>>): IActionInput => {
    return new GetSearchOptionsInput();
};

/**
 * Get action.
 * @param input - Input data.
 * @param context - Action context.
 * @returns - List of search options.
 */
export async function getSearchOptionsAction(input: GetSearchOptionsInput, context: IActionContext): Promise<SearchOption[]> {
    if (!context) {
        throw new Error('getSearchOptionsAction - Action context cannot be null/undefined');
    }

    let searchOptions: SearchOption[] = [];

    try {
        searchOptions = await getSearchOptions()(context);
    } catch (error) {
        throw error;
    }

    return searchOptions;
}

/**
 * Get search options action.
 * @param id - Unique Id for data action.
 * @param context - Action context.
 * @param input - Input for data action.
 * @returns Api response.
 */
export const getSearchOptionsActionDataAction = createObservableDataAction<SearchOption[]>({
    id: '@msdyn365-commerce-modules/order-history-with-search-and-filters/get-search-options',
    action: getSearchOptionsAction as IAction<SearchOption[]>,
    input: createGetSearchOptionsInput
});

export default getSearchOptionsActionDataAction;
